// src/pages/ReclamoDetalle.jsx
import { ImageGallery } from "../components/common/ImageGallery";
import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import { Loading, Button, Card, Badge, Alert, Select } from "../components/common";
import { formatText, formatDate, formatRelativeTime } from "../components/common/formatHelpers";
import { ESTADOS_CONFIG, CATEGORIAS_CONFIG, PRIORIDADES_CONFIG, LA_PLATA_CENTER, DEFAULT_ZOOM, API_BASE_URL } from "../constants";
import { useAuth } from "../hooks/useAuth";
import apiService from "../services/api.service";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import imageService from "../services/image.service";

// Configurar iconos de Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

export default function ReclamoDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userData, hasAdminAccess, firebaseUser } = useAuth();

  const [reclamo, setReclamo] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [miFeedback, setMiFeedback] = useState(null);
  const [direccion, setDireccion] = useState("Cargando dirección...");
  const [nuevoComentario, setNuevoComentario] = useState("");
  const [loadingReclamo, setLoadingReclamo] = useState(true);
  const [loadingHistorial, setLoadingHistorial] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isUpdatingEstado, setIsUpdatingEstado] = useState(false);
  const [isUpdatingPrioridad, setIsUpdatingPrioridad] = useState(false);
  const [calidad, setCalidad] = useState(0);
  const [isSubmittingCalidad, setIsSubmittingCalidad] = useState(false);

  const handleEstadoChange = async (nuevoEstado) => {
    if (!hasAdminAccess) return;
    setIsUpdatingEstado(true);
    setErrorMessage("");

    try {
      await apiService.updateReclamoEstado(id, nuevoEstado);
      setReclamo(prev => ({ ...prev, estado: nuevoEstado }));
      setSuccessMessage("Estado actualizado correctamente");

      // Agregar al historial
      const cambio = {
        reclamo_id: parseInt(id),
        comentario: `Cambio de estado de ${formatText(reclamo.estado)} a ${formatText(nuevoEstado)}`
      };
      await apiService.createHistorial(cambio);

      // Actualizar historial
      const nuevoHistorial = await apiService.getHistorialByReclamo(id);
      setHistorial(nuevoHistorial);
    } catch (err) {
      setErrorMessage("Error al actualizar el estado: " + err.message);
    } finally {
      setIsUpdatingEstado(false);
      setTimeout(() => setSuccessMessage(""), 3000);
    }
  };

  const handlePrioridadChange = async (nuevaPrioridad) => {
    if (!hasAdminAccess) return;
    setIsUpdatingPrioridad(true);
    setErrorMessage("");

    try {
      await apiService.updateReclamoPrioridad(id, nuevaPrioridad);
      setReclamo(prev => ({ ...prev, prioridad: nuevaPrioridad }));
      setSuccessMessage("Prioridad actualizada correctamente");

      // Agregar al historial
      const cambio = {
        reclamo_id: parseInt(id),
        comentario: `Cambio de prioridad de ${formatText(reclamo.prioridad)} a ${formatText(nuevaPrioridad)}`
      };
      await apiService.createHistorial(cambio);

      // Actualizar historial
      const nuevoHistorial = await apiService.getHistorialByReclamo(id);
      setHistorial(nuevoHistorial);
    } catch (err) {
      setErrorMessage("Error al actualizar la prioridad: " + err.message);
    } finally {
      setIsUpdatingPrioridad(false);
      setTimeout(() => setSuccessMessage(""), 3000);
    }
  };

  // Verificar si el usuario actual es el creador del reclamo
  const isOwner = firebaseUser && reclamo && reclamo.usuario_uid === firebaseUser.uid;

  // Función para eliminar una imagen individual
  const handleDeleteImage = async (imagenId) => {
    try {
      const imagenAEliminar = reclamo.imagenes.find(img => img.id === imagenId);
      if (!imagenAEliminar) {
        throw new Error('Imagen no encontrada');
      }

      // 1. Eliminar de la base de datos
      await apiService.deleteImagen(imagenId);
      // 2. Eliminar de Firebase Storage
      await imageService.deleteImages([imagenAEliminar]);
      // 3. Actualizar el estado local
      setReclamo(prev => ({
        ...prev,
        imagenes: prev.imagenes.filter(img => img.id !== imagenId)
      }));
      setSuccessMessage('Imagen eliminada correctamente');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('❌ Error eliminando imagen:', error);
      setErrorMessage('No se pudo eliminar la imagen: ' + error.message);
      throw error; // Re-lanzar el error para que ImageGallery pueda manejarlo
    }
  };

  /*
    const handleAddImages = async (files) => {
      try {
        // 1. Subir imágenes a Firebase Storage
        const uploadedImages = await imageService.uploadImages(files);
        // 2. Crear registros en la base de datos
        const imageRecords = await imageService.createImageRecords(uploadedImages, parseInt(id));
        // 3. Actualizar el estado local
        setReclamo(prev => ({
          ...prev,
          imagenes: [...prev.imagenes, ...imageRecords]
        }));
  
        setSuccessMessage(`${files.length} imagen(es) agregada(s) correctamente`);
        setTimeout(() => setSuccessMessage(''), 3000);
  
      } catch (error) {
        setErrorMessage('No se pudieron agregar las imágenes: ' + error.message);
        throw error;
      }
    };
  */

  const handleSuscripcion = async () => {
    if (!firebaseUser) {
      setErrorMessage("Debes iniciar sesión para suscribirte");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      let updatedFeedback;
      if (miFeedback) {
        // Si ya existe un feedback, actualizar solo la suscripción
        updatedFeedback = await apiService.updateFeedback(miFeedback.id, {
          suscripcion: !miFeedback.suscripcion
        });
      } else {
        // Si no existe feedback, crear uno nuevo solo con suscripción
        updatedFeedback = await apiService.createFeedback({
          reclamo_id: parseInt(id),
          suscripcion: true
        });
      }

      setMiFeedback(updatedFeedback);
      setFeedbacks(prev => {
        const filtered = prev.filter(f => f.id !== updatedFeedback.id);
        return [...filtered, updatedFeedback];
      });

      setSuccessMessage(
        updatedFeedback.suscripcion
          ? "✅ Te has suscrito a las notificaciones de este reclamo"
          : "🔕 Te has desuscrito de las notificaciones"
      );
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      console.error("Error actualizando suscripción:", err);
      setErrorMessage("Error al actualizar la suscripción: " + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Cargar reclamo
  useEffect(() => {
    const fetchReclamo = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/reclamos/${id}`);
        if (!res.ok) throw new Error("Error al cargar reclamo");
        const data = await res.json();
        setReclamo(data);

        // Obtener dirección aproximada usando Nominatim (OpenStreetMap)
        if (data.latitud && data.longitud) {
          try {
            const geoRes = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${data.latitud}&lon=${data.longitud}&zoom=18&addressdetails=1`,
              {
                headers: {
                  'User-Agent': 'ReclamosApp/1.0'
                }
              }
            );
            if (geoRes.ok) {
              const geoData = await geoRes.json();
              const addr = geoData.address;
              const direccionFormateada = [
                addr.road,
                addr.house_number,
                addr.suburb || addr.neighbourhood,
                addr.city || addr.town || addr.village,
                addr.state
              ].filter(Boolean).join(', ');

              setDireccion(direccionFormateada || geoData.display_name);
            } else {
              setDireccion("No se pudo obtener la dirección");
            }
          } catch (err) {
            console.error("Error obteniendo dirección:", err);
            setDireccion("Dirección no disponible");
          }
        }
      } catch (err) {
        setErrorMessage("No se pudo obtener el reclamo: " + err.message);
      } finally {
        setLoadingReclamo(false);
      }
    };

    fetchReclamo();
  }, [id]);

  // Cargar historial
  useEffect(() => {
    const fetchHistorial = async () => {
      try {
        const data = await apiService.getHistorialByReclamo(id);
        setHistorial(data);
      } catch (err) {
        console.error("Error cargando historial:", err);
      } finally {
        setLoadingHistorial(false);
      }
    };

    fetchHistorial();
  }, [id]);

  // Cargar feedbacks
  useEffect(() => {
    const fetchFeedbacks = async () => {
      try {
        const data = await apiService.getFeedbacksByReclamo(id);
        setFeedbacks(data);

        // Buscar si el usuario actual ya votó - usar firebaseUser en lugar de userData
        if (firebaseUser) {
          const mioFeedback = data.find(f => f.usuario_uid === firebaseUser.uid);
          setMiFeedback(mioFeedback || null);
        }
      } catch (err) {
        console.error("Error cargando feedbacks:", err);
      }
    };

    fetchFeedbacks();
  }, [id, firebaseUser]);

  // Efecto para cargar la calificación existente
  useEffect(() => {
    if (miFeedback && miFeedback.calidad_solucion) {
      setCalidad(miFeedback.calidad_solucion);
    }
  }, [miFeedback]);

  // Función para manejar el cambio de calificación
  const handleCalidadChange = async (nuevaCalidad) => {
    if (!firebaseUser) {
      setErrorMessage("Debes iniciar sesión para calificar");
      return;
    }

    // Validar que la calificación esté entre 1-5
    if (nuevaCalidad < 1 || nuevaCalidad > 5) {
      setErrorMessage("La calificación debe ser entre 1 y 5 estrellas");
      return;
    }

    setIsSubmittingCalidad(true);
    setErrorMessage("");

    try {
      let updatedFeedback;
      if (miFeedback) {
        // Actualizar solo la calidad_solucion del feedback existente
        updatedFeedback = await apiService.updateFeedback(miFeedback.id, {
          calidad_solucion: nuevaCalidad
        });
      } else {
        // Crear nuevo feedback solo con la calificación
        updatedFeedback = await apiService.createFeedback({
          reclamo_id: parseInt(id),
          calidad_solucion: nuevaCalidad
        });
      }

      setMiFeedback(updatedFeedback);
      setFeedbacks(prev => {
        const filtered = prev.filter(f => f.id !== updatedFeedback.id);
        return [...filtered, updatedFeedback];
      });

      setSuccessMessage(`Calificación ${nuevaCalidad} estrellas guardada`);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      console.error("Error guardando calificación:", err);

      // Manejo específico de errores del backend
      if (err.message.includes("ya envió feedback")) {
        setErrorMessage("Ya has calificado este reclamo");
      } else if (err.message.includes("Debes iniciar sesión") || err.message.includes("No autorizado")) {
        setErrorMessage("Debes iniciar sesión para calificar");
      } else if (err.message.includes("no existe")) {
        setErrorMessage("El reclamo no existe");
      } else {
        setErrorMessage("Error al guardar la calificación: " + err.message);
      }
    } finally {
      setIsSubmittingCalidad(false);
    }
  };

  const handleAgregarComentario = async (e) => {
    e.preventDefault();

    if (!nuevoComentario.trim()) {
      setErrorMessage("El comentario no puede estar vacío");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const nuevoHistorial = await apiService.createHistorial({
        reclamo_id: parseInt(id),
        comentario: nuevoComentario
      });

      setHistorial(prev => [...prev, nuevoHistorial]);
      setNuevoComentario("");
      setSuccessMessage("Comentario agregado correctamente");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setErrorMessage(err.message || "Error al agregar comentario");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVotar = async (existe) => {
    if (!firebaseUser) {
      setErrorMessage("Debes iniciar sesión para votar");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      // Si ya votó lo mismo, eliminar el voto
      if (miFeedback && miFeedback.existe === existe) {
        await apiService.deleteFeedback(miFeedback.id);
        setMiFeedback(null);
        setFeedbacks(prev => prev.filter(f => f.id !== miFeedback.id));
        setSuccessMessage("Has eliminado tu voto");
      } else if (miFeedback) {
        // Actualizar voto existente a la opción contraria
        const updated = await apiService.updateFeedback(miFeedback.id, {
          existe
        });
        setMiFeedback(updated);
        setFeedbacks(prev => prev.map(f => f.id === updated.id ? updated : f));
        setSuccessMessage(`Has cambiado tu voto a "${existe ? 'Existe' : 'No existe'}"`);
      } else {
        // Crear nuevo voto
        const newFeedback = await apiService.createFeedback({
          reclamo_id: parseInt(id),
          existe
        });
        setMiFeedback(newFeedback);
        setFeedbacks(prev => [...prev, newFeedback]);
        setSuccessMessage(`Has votado "${existe ? 'Existe' : 'No existe'}"`);
      }
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      console.error("Error al votar:", err);
      setErrorMessage(err.message || "Error al votar");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calcular estadísticas de votos
  const calcularEstadisticas = () => {
    const totalVotos = feedbacks.filter(f => f.existe !== null).length;
    const votosExiste = feedbacks.filter(f => f.existe === true).length;
    const votosNoExiste = feedbacks.filter(f => f.existe === false).length;
    const porcentajeExiste = totalVotos > 0 ? Math.round((votosExiste / totalVotos) * 100) : 0;

    return { totalVotos, votosExiste, votosNoExiste, porcentajeExiste };
  };

  const stats = calcularEstadisticas();


  if (loadingReclamo) {
    return <Loading text="Cargando el reclamo..." />;
  }

  if (!reclamo) {
    return (
      <div style={{ padding: "24px", maxWidth: "800px", margin: "0 auto" }}>
        <Alert variant="error">
          No se pudo cargar el reclamo
        </Alert>
        <Button variant="secondary" onClick={() => navigate(-1)} style={{ marginTop: "16px" }}>
          ← Volver al mapa
        </Button>
      </div>
    );
  }

  const StarRating = ({ rating, onRatingChange, disabled = false, size = 32 }) => {
    const [hoverRating, setHoverRating] = useState(0);

    return (
      <div style={{
        display: 'flex',
        gap: '8px',
        justifyContent: 'center',
        margin: '16px 0',
        flexDirection: 'column',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          {[1, 2, 3, 4, 5].map((star) => {
            const isFilled = star <= (hoverRating || rating);
            return (
              <button
                key={star}
                type="button"
                disabled={disabled}
                onClick={() => onRatingChange(star)}
                onMouseEnter={() => !disabled && setHoverRating(star)}
                onMouseLeave={() => !disabled && setHoverRating(0)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  fontSize: `${size}px`,
                  color: isFilled ? '#FFD700' : '#e2e8f0',
                  transition: 'all 0.2s ease',
                  transform: isFilled && !disabled ? 'scale(1.1)' : 'scale(1)',
                  filter: disabled ? 'grayscale(100%)' : 'none',
                  padding: '4px'
                }}
              >
                {isFilled ? '★' : '☆'}
              </button>
            );
          })}
        </div>

        {/* Indicador de calificación mínima */}
        <div style={{
          fontSize: '12px',
          color: '#64748b',
          marginTop: '8px'
        }}>
          Toca una estrella para calificar (1-5)
        </div>
      </div>
    );
  };

  const estadoConfig = ESTADOS_CONFIG.find((e) => e.value === reclamo.estado);
  const categoriaConfig = CATEGORIAS_CONFIG.find((c) => c.value === reclamo.categoria);

  return (
    <div style={{ padding: "24px", maxWidth: "1000px", margin: "0 auto" }}>
      {/* Mensajes */}
      {successMessage && (
        <Alert variant="success" onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert variant="error" onClose={() => setErrorMessage("")}>
          {errorMessage}
        </Alert>
      )}

      <div style={{ marginBottom: "24px" }}>
        <Button
          onClick={() => navigate(-1)}
        >
          ← Volver
        </Button>
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr",
        gap: "24px"
      }}>
        {/* Información Principal */}
        <Card padding="32px">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "20px" }}>
            <div>
              <h1 style={{
                color: "#1e293b",
                margin: "0 0 8px 0",
                fontSize: "32px",
                fontWeight: "700"
              }}>
                {categoriaConfig?.icon} {reclamo.titulo}
              </h1>
              <p style={{ color: "#64748b", margin: 0, fontSize: "16px" }}>
                Creado el {formatDate(reclamo.fecha_creacion)}
                {isOwner && " • Sos el creador de este reclamo"}
                {reclamo.estado === "resuelto" && reclamo.fecha_resolucion && (
                  <>
                    <br />
                    <span style={{ color: "#059669", fontWeight: "600" }}>
                      ✅ Resuelto el {formatDate(reclamo.fecha_resolucion)}
                      {reclamo.dias_para_resolucion !== null && ` (${reclamo.dias_para_resolucion.toFixed(1)} días)`}
                    </span>
                  </>
                )}
              </p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              {reclamo.promedio_calificacion > 0 && (
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: "6px 12px",
                  backgroundColor: "#fff7ed",
                  border: "1px solid #ffedd5",
                  borderRadius: "20px",
                  color: "#9a3412",
                  fontWeight: "700",
                  fontSize: "14px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
                }} title="Calificación media de la solución">
                  <span style={{ color: "#f59e0b", fontSize: "16px" }}>★</span>
                  <span>{reclamo.promedio_calificacion.toFixed(1)}</span>
                </div>
              )}
              <Badge variant={
                estadoConfig?.value === "resuelto" ? "success" :
                  estadoConfig?.value === "en_progreso" ? "info" : "warning"
              }>
                {estadoConfig?.label || formatText(reclamo.estado)}
              </Badge>
            </div>
          </div>

          <p style={{
            color: "#475569",
            fontSize: "16px",
            lineHeight: "1.6",
            marginBottom: "24px",
            padding: "16px",
            backgroundColor: "#f8fafc",
            borderRadius: "8px"
          }}>
            {reclamo.descripcion}
          </p>

          {/* GALERÍA DE IMÁGENES */}
          <ImageGallery
            imagenes={reclamo.imagenes || []}
            onDeleteImage={handleDeleteImage}
            onAddImages={null} // Deshabilitado temporalmente
            isOwner={isOwner}
          />

          {/* Detalles */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "24px",
              padding: "20px",
              backgroundColor: "#f8fafc",
              borderRadius: "12px",
              alignItems: "center",
              justifyContent: "space-between"
            }}
          >
            {/* Categoría */}
            <div style={{ flex: "1 1 200px", minWidth: "200px" }}>
              <div
                style={{
                  fontSize: "13px",
                  color: "#64748b",
                  marginBottom: "4px",
                  fontWeight: "600"
                }}
              >
                Categoría
              </div>
              <div
                style={{
                  fontSize: "15px",
                  color: "#1e293b",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px"
                }}
              >
                <span style={{ fontSize: "20px" }}>{categoriaConfig?.icon}</span>
                <span>{categoriaConfig?.label}</span>
              </div>
            </div>

            {/* Ubicación */}
            <div style={{ flex: "1 1 300px", minWidth: "250px" }}>
              <div
                style={{
                  fontSize: "13px",
                  color: "#64748b",
                  marginBottom: "4px",
                  fontWeight: "600"
                }}
              >
                Ubicación
              </div>
              <div
                style={{
                  fontSize: "15px",
                  color: "#1e293b",
                  marginBottom: "4px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px"
                }}
              >
                <span>📍</span>
                <span>{direccion}</span>
              </div>
              <div
                style={{
                  fontSize: "13px",
                  color: "#64748b",
                  fontFamily: "monospace"
                }}
              >
                {reclamo.latitud?.toFixed(4)}, {reclamo.longitud?.toFixed(4)}
              </div>
            </div>

            {/* ID del Reclamo */}
            <div
              style={{
                flex: "1 1 100px",
                minWidth: "120px",
                textAlign: "center"
              }}
            >
              <div
                style={{
                  fontSize: "13px",
                  color: "#64748b",
                  marginBottom: "4px",
                  fontWeight: "600"
                }}
              >
                ID del Reclamo
              </div>
              <div
                style={{
                  fontSize: "18px",
                  color: "#1e293b",
                  fontWeight: "700"
                }}
              >
                #{reclamo.id}
              </div>
            </div>
          </div>

          {hasAdminAccess && (
            <div style={{
              marginTop: "20px",
              padding: "20px",
              backgroundColor: "#f1f5f9",
              borderRadius: "8px",
              gridColumn: "1/-1"
            }}>
              <div style={{ fontSize: "14px", color: "#475569", marginBottom: "12px", fontWeight: "600" }}>
                Gestión del Reclamo
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                <div>
                  <Select
                    label="Estado"
                    value={reclamo.estado}
                    onChange={(e) => handleEstadoChange(e.target.value)}
                    options={ESTADOS_CONFIG.map(estado => ({
                      value: estado.value,
                      label: estado.label
                    }))}
                    disabled={isUpdatingEstado}
                  />
                </div>
                <div>
                  <Select
                    label="Prioridad"
                    value={reclamo.prioridad}
                    onChange={(e) => handlePrioridadChange(e.target.value)}
                    options={PRIORIDADES_CONFIG.map(prioridad => ({
                      value: prioridad.value,
                      label: prioridad.label
                    }))}
                    disabled={isUpdatingPrioridad}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Botón para eliminar reclamo */}
          {(isOwner) && (
            <div style={{ alignSelf: "center" }}>
            </div>
          )}
        </Card>

        {/* Mapa */}
        <Card padding="0" style={{ overflow: "hidden" }}>
          <div style={{ height: "400px", width: "100%" }}>
            <MapContainer
              center={
                reclamo.latitud && reclamo.longitud
                  ? [reclamo.latitud, reclamo.longitud]
                  : LA_PLATA_CENTER
              }
              zoom={DEFAULT_ZOOM + 2}
              style={{ height: "100%", width: "100%" }}
              scrollWheelZoom={false}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a>'
              />
              <Marker position={[reclamo.latitud, reclamo.longitud]}>
                <Popup>
                  <strong>{reclamo.titulo}</strong>
                  <br />
                  {formatText(reclamo.estado)}
                </Popup>
              </Marker>
            </MapContainer>
          </div>
        </Card>

        {/* Sistema de Votación - Solo si NO está resuelto */}
        {reclamo.estado !== "resuelto" && (
          <Card padding="32px">
            <h2 style={{
              fontSize: "24px",
              fontWeight: "700",
              color: "#1e293b",
              margin: "0 0 16px 0"
            }}>
              🗳️ Validación Ciudadana
            </h2>

            <p style={{
              color: "#64748b",
              fontSize: "15px",
              marginBottom: "24px"
            }}>
              Ayuda a verificar este reclamo votando si existe o no en la ubicación indicada
            </p>

            {/* Estadísticas de votación */}
            {stats.totalVotos > 0 && (
              <div style={{
                padding: "20px",
                backgroundColor: "#f8fafc",
                borderRadius: "12px",
                marginBottom: "24px"
              }}>
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                  gap: "16px",
                  marginBottom: "16px"
                }}>
                  <div style={{ textAlign: "center" }}>
                    <div style={{
                      fontSize: "32px",
                      fontWeight: "700",
                      color: "#00a680",
                      marginBottom: "4px"
                    }}>
                      {stats.totalVotos}
                    </div>
                    <div style={{ fontSize: "13px", color: "#64748b", fontWeight: "600" }}>
                      Total de votos
                    </div>
                  </div>

                  <div style={{ textAlign: "center" }}>
                    <div style={{
                      fontSize: "32px",
                      fontWeight: "700",
                      color: "#22c55e",
                      marginBottom: "4px"
                    }}>
                      {stats.votosExiste}
                    </div>
                    <div style={{ fontSize: "13px", color: "#64748b", fontWeight: "600" }}>
                      👍 Existe
                    </div>
                  </div>

                  <div style={{ textAlign: "center" }}>
                    <div style={{
                      fontSize: "32px",
                      fontWeight: "700",
                      color: "#ef4444",
                      marginBottom: "4px"
                    }}>
                      {stats.votosNoExiste}
                    </div>
                    <div style={{ fontSize: "13px", color: "#64748b", fontWeight: "600" }}>
                      👎 No existe
                    </div>
                  </div>
                </div>

                {/* Barra de progreso */}
                <div style={{
                  height: "12px",
                  backgroundColor: "#e2e8f0",
                  borderRadius: "6px",
                  overflow: "hidden",
                  display: "flex"
                }}>
                  <div style={{
                    width: `${stats.porcentajeExiste}%`,
                    backgroundColor: "#22c55e",
                    transition: "width 0.3s ease"
                  }} />
                  <div style={{
                    width: `${100 - stats.porcentajeExiste}%`,
                    backgroundColor: "#ef4444",
                    transition: "width 0.3s ease"
                  }} />
                </div>
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: "8px",
                  fontSize: "13px",
                  fontWeight: "600"
                }}>
                  <span style={{ color: "#22c55e" }}>{stats.porcentajeExiste}% Existe</span>
                  <span style={{ color: "#ef4444" }}>{100 - stats.porcentajeExiste}% No existe</span>
                </div>
              </div>
            )}

            {/* Botones de votación Y suscripción */}
            {userData ? (
              <div>
                {miFeedback && (
                  <div style={{
                    padding: "12px 16px",
                    backgroundColor: "#dbeafe",
                    borderRadius: "8px",
                    marginBottom: "16px",
                    fontSize: "14px",
                    color: "#1e40af",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px"
                  }}>
                    <span>ℹ️</span>
                    <span>Tu voto: <strong>{miFeedback.existe ? "👍 Existe" : "👎 No existe"}</strong>. Presiona el mismo botón para eliminarlo.</span>
                  </div>
                )}

                <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "16px" }}>
                  <button
                    onClick={() => handleVotar(true)}
                    disabled={isSubmitting}
                    style={{
                      flex: 1,
                      minWidth: "200px",
                      padding: "20px 24px",
                      fontSize: "18px",
                      fontWeight: "700",
                      borderRadius: "12px",
                      border: miFeedback?.existe === true ? "3px solid #16a34a" : "3px solid #86efac",
                      backgroundColor: miFeedback?.existe === true ? "#22c55e" : "white",
                      color: miFeedback?.existe === true ? "white" : "#16a34a",
                      cursor: isSubmitting ? "not-allowed" : "pointer",
                      transition: "all 0.3s ease",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "12px",
                      boxShadow: miFeedback?.existe === true
                        ? "0 8px 24px rgba(34, 197, 94, 0.4)"
                        : "0 2px 8px rgba(0, 0, 0, 0.05)",
                      transform: "translateY(0)"
                    }}
                    onMouseEnter={(e) => {
                      if (!isSubmitting) {
                        e.target.style.transform = "translateY(-4px)";
                        e.target.style.boxShadow = miFeedback?.existe === true
                          ? "0 12px 32px rgba(34, 197, 94, 0.5)"
                          : "0 8px 20px rgba(34, 197, 94, 0.3)";
                        if (miFeedback?.existe !== true) {
                          e.target.style.backgroundColor = "#f0fdf4";
                        }
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSubmitting) {
                        e.target.style.transform = "translateY(0)";
                        e.target.style.boxShadow = miFeedback?.existe === true
                          ? "0 8px 24px rgba(34, 197, 94, 0.4)"
                          : "0 2px 8px rgba(0, 0, 0, 0.05)";
                        if (miFeedback?.existe !== true) {
                          e.target.style.backgroundColor = "white";
                        }
                      }
                    }}
                  >
                    <span style={{ fontSize: "28px" }}>👍</span>
                    <span>{miFeedback?.existe === true ? "Has votado: Existe" : "Votar: Existe"}</span>
                  </button>

                  <button
                    onClick={() => handleVotar(false)}
                    disabled={isSubmitting}
                    style={{
                      flex: 1,
                      minWidth: "200px",
                      padding: "20px 24px",
                      fontSize: "18px",
                      fontWeight: "700",
                      borderRadius: "12px",
                      border: miFeedback?.existe === false ? "3px solid #dc2626" : "3px solid #fca5a5",
                      backgroundColor: miFeedback?.existe === false ? "#ef4444" : "white",
                      color: miFeedback?.existe === false ? "white" : "#dc2626",
                      cursor: isSubmitting ? "not-allowed" : "pointer",
                      transition: "all 0.3s ease",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "12px",
                      boxShadow: miFeedback?.existe === false
                        ? "0 8px 24px rgba(239, 68, 68, 0.4)"
                        : "0 2px 8px rgba(0, 0, 0, 0.05)",
                      transform: "translateY(0)"
                    }}
                    onMouseEnter={(e) => {
                      if (!isSubmitting) {
                        e.target.style.transform = "translateY(-4px)";
                        e.target.style.boxShadow = miFeedback?.existe === false
                          ? "0 12px 32px rgba(239, 68, 68, 0.5)"
                          : "0 8px 20px rgba(239, 68, 68, 0.3)";
                        if (miFeedback?.existe !== false) {
                          e.target.style.backgroundColor = "#fef2f2";
                        }
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSubmitting) {
                        e.target.style.transform = "translateY(0)";
                        e.target.style.boxShadow = miFeedback?.existe === false
                          ? "0 8px 24px rgba(239, 68, 68, 0.4)"
                          : "0 2px 8px rgba(0, 0, 0, 0.05)";
                        if (miFeedback?.existe !== false) {
                          e.target.style.backgroundColor = "white";
                        }
                      }
                    }}
                  >
                    <span style={{ fontSize: "28px" }}>👎</span>
                    <span>{miFeedback?.existe === false ? "Has votado: No existe" : "Votar: No existe"}</span>
                  </button>
                </div>

                {/* NUEVO: Botón de Suscripción/Notificaciones */}
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <button
                    onClick={() => handleSuscripcion()}
                    disabled={isSubmitting}
                    style={{
                      padding: "16px 32px",
                      fontSize: "16px",
                      fontWeight: "600",
                      borderRadius: "12px",
                      border: miFeedback?.suscripcion ? "2px solid #00a680" : "2px solid #cbd5e1",
                      backgroundColor: miFeedback?.suscripcion ? "#00a680" : "white",
                      color: miFeedback?.suscripcion ? "white" : "#475569",
                      cursor: isSubmitting ? "not-allowed" : "pointer",
                      transition: "all 0.3s ease",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "12px",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                      transform: "translateY(0)"
                    }}
                    onMouseEnter={(e) => {
                      if (!isSubmitting) {
                        e.target.style.transform = "translateY(-2px)";
                        e.target.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.1)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSubmitting) {
                        e.target.style.transform = "translateY(0)";
                        e.target.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.05)";
                      }
                    }}
                  >
                    <span style={{ fontSize: "20px" }}>
                      {miFeedback?.suscripcion ? "🔔" : "🔕"}
                    </span>
                    <span>
                      {isSubmitting
                        ? "Procesando..."
                        : miFeedback?.suscripcion
                          ? "Suscrito a notificaciones"
                          : "Obtener notificaciones"
                      }
                    </span>
                  </button>
                </div>

                <p style={{
                  textAlign: "center",
                  fontSize: "14px",
                  color: "#64748b",
                  marginTop: "12px",
                  marginBottom: "0"
                }}>
                  {miFeedback?.suscripcion
                    ? "Recibirás actualizaciones cuando este reclamo cambie de estado"
                    : "Recibe notificaciones cuando este reclamo se resuelva o tenga novedades"
                  }
                </p>
              </div>
            ) : (
              <div style={{
                padding: "16px",
                backgroundColor: "#fef3c7",
                borderRadius: "8px",
                fontSize: "14px",
                color: "#92400e",
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}>
                <span>⚠️</span>
                <span>Debes <strong>iniciar sesión</strong> para poder votar o suscribirte</span>
              </div>
            )}
          </Card>
        )}

        {/* Sistema de Calificación - Solo si está resuelto y es ciudadano*/}
        {reclamo.estado === "resuelto" && (
          <Card padding="32px">
            <h2 style={{
              fontSize: "24px",
              fontWeight: "700",
              color: "#1e293b",
              margin: "0 0 16px 0",
              textAlign: "center"
            }}>
              ⭐ Califica la Calidad de la Solución
            </h2>

            <p style={{
              color: "#64748b",
              fontSize: "15px",
              marginBottom: "24px",
              textAlign: "center"
            }}>
              ¿Qué tan satisfecho estás con la solución de este reclamo?
            </p>

            {/* Mostrar calificación actual si existe */}
            {miFeedback?.calidad_solucion > 0 && (
              <div style={{
                padding: "12px 16px",
                backgroundColor: "#f0fdf4",
                borderRadius: "8px",
                marginBottom: "16px",
                fontSize: "14px",
                color: "#166534",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px"
              }}>
                <span>✅</span>
                <span>
                  Tu calificación actual: <strong>{miFeedback.calidad_solucion} estrellas</strong>
                </span>
              </div>
            )}

            <StarRating
              rating={calidad}
              onRatingChange={handleCalidadChange}
              disabled={isSubmittingCalidad}
              size={40}
            />

            {/* Texto descriptivo debajo de las estrellas */}
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "8px",
              fontSize: "14px",
              color: "#64748b"
            }}>
              <span>Muy mala</span>
              <span>Excelente</span>
            </div>

            {isSubmittingCalidad && (
              <div style={{
                textAlign: "center",
                marginTop: "16px"
              }}>
                <div style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 16px",
                  backgroundColor: "#f8fafc",
                  borderRadius: "8px",
                  fontSize: "14px",
                  color: "#64748b"
                }}>
                  <div style={{
                    width: "16px",
                    height: "16px",
                    border: "2px solid #e2e8f0",
                    borderTop: "2px solid #00a680",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite"
                  }}></div>
                  Guardando calificación...
                </div>
              </div>
            )}

            <style>
              {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
            </style>
          </Card>
        )}

        {/* Historial de Cambios */}
        <Card padding="32px">
          <h2 style={{
            fontSize: "24px",
            fontWeight: "700",
            color: "#1e293b",
            margin: "0 0 24px 0"
          }}>
            📋 Historial de Cambios
          </h2>

          {loadingHistorial ? (
            <Loading text="Cargando historial..." />
          ) : historial.length === 0 ? (
            <div style={{
              textAlign: "center",
              padding: "40px 20px",
              backgroundColor: "#f8fafc",
              borderRadius: "12px"
            }}>
              <div style={{ fontSize: "48px", marginBottom: "12px" }}>📝</div>
              <p style={{ color: "#64748b", margin: 0 }}>
                Aún no hay comentarios en este reclamo
              </p>
            </div>
          ) : (
            <div style={{
              display: "flex",
              flexDirection: "column",
              gap: "16px"
            }}>
              {historial.map((entrada) => (
                <div
                  key={entrada.id}
                  style={{
                    padding: "16px",
                    backgroundColor: "#f8fafc",
                    borderRadius: "12px",
                    borderLeft: "4px solid #00a680"
                  }}
                >
                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "start",
                    marginBottom: "8px"
                  }}>
                    <div style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#1e293b"
                    }}>
                      👤 {entrada.usuario?.nombre || entrada.usuario?.email || entrada.usuario_uid}
                    </div>
                    <div style={{
                      fontSize: "13px",
                      color: "#64748b"
                    }}>
                      {formatRelativeTime(entrada.fecha)}
                    </div>
                  </div>
                  <p style={{
                    margin: 0,
                    color: "#475569",
                    fontSize: "15px",
                    lineHeight: "1.5"
                  }}>
                    {entrada.comentario}
                  </p>
                  <div style={{
                    fontSize: "12px",
                    color: "#94a3b8",
                    marginTop: "8px"
                  }}>
                    {formatDate(entrada.fecha)}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Formulario para agregar comentario - Solo administradores */}
          {hasAdminAccess && (
            <div style={{
              marginTop: "24px",
              paddingTop: "24px",
              borderTop: "2px solid #e2e8f0"
            }}>
              <h3 style={{
                fontSize: "18px",
                fontWeight: "600",
                color: "#1e293b",
                margin: "0 0 16px 0"
              }}>
                💬 Agregar Comentario
              </h3>
              <form onSubmit={handleAgregarComentario}>
                <textarea
                  placeholder="Escribe un comentario sobre este reclamo..."
                  value={nuevoComentario}
                  onChange={(e) => setNuevoComentario(e.target.value)}
                  disabled={isSubmitting}
                  style={{
                    width: "100%",
                    minHeight: "100px",
                    padding: "12px 16px",
                    fontSize: "15px",
                    border: "2px solid #e2e8f0",
                    borderRadius: "8px",
                    outline: "none",
                    transition: "all 0.2s ease",
                    fontFamily: "inherit",
                    resize: "vertical",
                    boxSizing: "border-box",
                    marginBottom: "12px"
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#00a680";
                    e.target.style.boxShadow = "0 0 0 3px rgba(0, 166, 128, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e2e8f0";
                    e.target.style.boxShadow = "none";
                  }}
                />
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isSubmitting || !nuevoComentario.trim()}
                  icon="💬"
                >
                  {isSubmitting ? "Agregando..." : "Agregar Comentario"}
                </Button>
              </form>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}